<template>
    <div
        :aria-labelledby="`id-accessible-label-${id}`"
        :data-radio-value="radioValue"
        class="accessible-radio"
        role="radiogroup"
        tabindex="0"
        ref="radiogroup"
        @focus="onFocus"
    >
        <label
            class="label mb-3"
            tabindex="-1"
            :aria-label="stripHtmlTags(formattedAriaLabel(label, mandatory))"
            :id="`id-accessible-label-${id}`"
            v-html="formattedLabel(label, mandatory)" />
        <div
            v-for="option in options"
            :data-radio="option.value"
            role="radio"
            aria-checked="false"
            tabindex="-1"
            :aria-label="ariaLabelForRadio(option.label)"
            :class="`radio-option ${radiosClass}`"
            class="ml-6 mb-2"
            :key="option.value"
            @click="setChecked(option.value)"
            @keydown="handleKeydown"
        >
            <v-icon x-small class="mr-1" color="transparent">
                circle
            </v-icon>
            {{ option.label }}
        </div>
        <div v-if="errorMessage" class="error-text">
            {{ errorMessage }}
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { inputHelpers } from '@/mixins/inputHelpers';

export default {
    name: 'AccessibleRadioGroup',
    mixins: [inputHelpers],
    props: {
        label: { type: String, required: false },
        mandatory: { type: Boolean, default: false },
        options: { type: Array, required: true },
        radiosClass: { type: String, default: '' },
        rules: { type: Array, default: () => [] },
        value: { type: Number, default: null, required: false },
    },
    computed: {
        ...mapGetters({
            locale: 'locale',
        }),
    },
    data() {
        return {
            id: null,
            errorMessage: '',
            radioValue: null,
        };
    },
    mounted() {
        this.id = this._uid;
        if(this.value) this.setChecked(this.value);
    },
    methods: {
        validate() {
            const failedRule = this.rules.find((rule) => !rule(this.radioValue));
            this.errorMessage = failedRule ? failedRule(this.radioValue) : '';
        },
        setChecked(newValue) {
            const parsedValue = isNaN(newValue) ? newValue : Number(newValue);
            if(parsedValue !== this.radioValue) this.radioValue = parsedValue;
            this.$refs.radiogroup.setAttribute('data-radio-value', parsedValue);

            this.options.forEach((option) => {
                const rb = this.$refs.radiogroup.querySelector(`[data-radio="${option.value}"]`);
                rb.setAttribute('aria-checked', option.value === parsedValue ? 'true' : 'false');
                rb.tabIndex = option.value === parsedValue ? 0 : -1;
            });

            this.validate();
            this.$emit('update:value', parsedValue);
            this.$emit('input', parsedValue);
        },
        handleKeydown(event) {
            const keys = { ArrowUp: -1, ArrowLeft: -1, ArrowDown: 1, ArrowRight: 1 };
            const direction = keys[event.key];
            if (direction) {
                const currentIndex = this.options.findIndex((o) => o.value === this.radioValue);
                const newIndex = currentIndex + direction;
                if (newIndex >= 0 && newIndex < this.options.length) {
                    this.setChecked(this.options[newIndex].value);
                }
                event.preventDefault();
            }
        },
        onFocus() {
            this.$refs.radiogroup.setAttribute('tabindex', '-1');
            this.$refs.radiogroup.querySelector('[role="radio"]').setAttribute('tabindex', '0');
        },
        stripHtmlTags(input) {
            return input.replace(/<[^>]*>/g, '');
        },
        ariaLabelForRadio(label) {
            return label + (this.locale === 'fr' ? ' Appuyez sur Espace pour sélectionner' : ' Press Space to select');
        }
    },
};
</script>

<style lang="scss" scoped>
.accessible-radio {
    font-size: 16px !important;
    label {
        display: block;
    }
    div {
        padding: 2px 0;
        &[aria-checked='true'] .v-icon {
            outline: 2px solid #00ccf2 !important;
        }
        .v-icon {
            outline: 2px solid grey !important;
            padding: 3px;
            border-radius: 50%;
        }
    }
}

.accessible-radio div[role='radio'] .v-icon:before {
    transform: scale(1.2);
    content: '';
    position: absolute;
    height: 2.5em;
    width: 2.5em;
    border-radius: 50%;
    opacity: 0.3;
}

.accessible-radio div[aria-checked='true']:hover .v-icon:before {
    background: #00ccf2 !important;
}

.accessible-radio div[role='radio']:hover .v-icon:before {
    background: grey;
    cursor: pointer;
}

.accessible-radio div[aria-checked='true'] .v-icon {
    color: #00ccf2 !important;
}

.error-text {
    color: red;
    font-size: 14px;
    margin-top: 2px;
}
</style>
